import React from 'react'
import { Text } from '../ui'

export const RECORD_LIST_PAGE_SIZE = 20

type PageSlice<T> = {
  page: number
  totalPages: number
  startIndex: number
  endIndex: number
  total: number
  items: T[]
}

export function sliceRecordPage<T>(
  items: T[],
  page: number,
  pageSize = RECORD_LIST_PAGE_SIZE
): PageSlice<T> {
  const total = items.length
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1)
  const currentPage = Math.min(Math.max(1, page), totalPages)
  const startIndex = (currentPage - 1) * pageSize
  return {
    page: currentPage,
    totalPages,
    startIndex,
    endIndex: Math.min(startIndex + pageSize, total),
    total,
    items: items.slice(startIndex, startIndex + pageSize),
  }
}

export function useRecordListPage(resetKey: string) {
  const [page, setPage] = React.useState(1)
  const [key, setKey] = React.useState(resetKey)
  if (key !== resetKey) {
    setKey(resetKey)
    setPage(1)
  }
  return [page, setPage] as const
}

type Props = {
  page: number
  totalPages: number
  startIndex: number
  endIndex: number
  total: number
  onPageChange: (page: number) => void
}

export const RecordListPagination: React.FC<Props> = ({
  page,
  totalPages,
  startIndex,
  endIndex,
  total,
  onPageChange,
}) => {
  if (total === 0) return null

  return (
    <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <Text size="sm" className="text-gray-700 dark:text-gray-300">
          Mostrando {startIndex + 1} a {endIndex} de {total} registros
        </Text>
        {totalPages > 1 && (
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Anterior
            </button>
            <div className="flex space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                let pageNumber: number
                if (totalPages <= 5) {
                  pageNumber = index + 1
                } else if (page <= 3) {
                  pageNumber = index + 1
                } else if (page >= totalPages - 2) {
                  pageNumber = totalPages - 4 + index
                } else {
                  pageNumber = page - 2 + index
                }
                return (
                  <button
                    key={pageNumber}
                    type="button"
                    onClick={() => onPageChange(pageNumber)}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      page === pageNumber
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    {pageNumber}
                  </button>
                )
              })}
            </div>
            <button
              type="button"
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Próximo
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
